import React, { memo, useMemo } from "react";
import makeLink from "../../utils/makeLink";
import validEmail from "../../utils/validEmail";
import { ThemeButton } from "../CoreUI";
import "./order-confirmation.css";
import { FiMail } from "react-icons/fi";
import { FaTruck, FaTwitter } from "react-icons/fa";
import { TiSocialFacebookCircular as FbIcon } from "react-icons/ti";
import { AiFillInstagram } from "react-icons/ai";

import { eventTypes } from "../../hooks/useTrack";
import useData from "../../hooks/useData";
import inPreview from "../../utils/inPreview";
import accessibilityProvider from "../../utils/accessibilityProvider";
import { PaddedContainer, SPACING, genClassName } from "@disco/disco_core";
import useExperience from "../../hooks/useExperience";
import usePing, { PING_TYPES } from "../../hooks/usePing";

const OrderConfirmationAction = memo(() => {
	const {
		publisher_details: {
			facebook = false,
			twitter = false,
			instagram = false,
		} = {},
		scrapedInformation,
	} = useData();

	const { shouldIncreaseImpressions } = useExperience();

	const { trackingLink, contactEmail } = useMemo(
		() => scrapedInformation || {},
		[scrapedInformation]
	);

	const fbLink = makeLink(facebook, "facebook.com");
	const twitterLink = makeLink(twitter, "twitter.com");
	const instaLink = makeLink(instagram, "instagram.com");

	const { ping: handleMinimize } = usePing({
		key: PING_TYPES.SHOW_ORDER_CLICK,
	});

	const hasActions = useMemo(
		() =>
			inPreview ||
			validEmail(contactEmail) ||
			trackingLink ||
			instaLink ||
			twitterLink ||
			fbLink ||
			shouldIncreaseImpressions,
		[
			contactEmail,
			fbLink,
			instaLink,
			trackingLink,
			twitterLink,
			shouldIncreaseImpressions,
		]
	);

	const actions = useMemo(
		() => ({
			contact_us: {
				children: shouldIncreaseImpressions ? "" : "Contact Us",
				event: eventTypes.WIDGET_CONTACT_US_CLICK,
				link: inPreview ? "" : `mailto:${contactEmail}`,
				icon: <FiMail />,
				iconOnly: shouldIncreaseImpressions,
				iconStyle: shouldIncreaseImpressions
					? { fontSize: "1.6rem" }
					: {},
				...accessibilityProvider({ name: "Contact Us" }),
				isVisible: validEmail(contactEmail) || inPreview,
				increaseImpressionsOrder: 3,
			},
			track_order: {
				children: "Track Order",
				event: eventTypes.WIDGET_TRACK_ORDER_CLICK,
				link: inPreview ? "" : trackingLink,
				icon: <FaTruck />,
				...accessibilityProvider({ name: "Track Order" }),
				isVisible:
					(trackingLink || inPreview) && !shouldIncreaseImpressions,
				increaseImpressionsOrder: 4,
			},
			instagram: {
				event: eventTypes.WIDGET_SHOP_INSTA_CLICK,
				link: instaLink,
				iconOnly: true,
				iconStyle: { fontSize: "1.6rem" },
				icon: <AiFillInstagram />,
				linkProps: accessibilityProvider({
					name: "Instagram",
				}),
				...accessibilityProvider({ name: "Instagram" }),
				isVisible: instaLink,
				increaseImpressionsOrder: 0,
			},
			twitter: {
				event: eventTypes.WIDGET_SHOP_TWITTER_CLICK,
				link: twitterLink,
				iconOnly: true,
				icon: <FaTwitter />,
				...accessibilityProvider({ name: "Twitter" }),
				isVisible: twitterLink,
				increaseImpressionsOrder: 1,
			},
			facebook: {
				event: eventTypes.WIDGET_SHOP_FB_CLICK,
				link: fbLink,
				iconOnly: true,
				icon: <FbIcon />,
				iconStyle: {
					fontSize: "1.8rem",
				},
				...accessibilityProvider({ name: "Facebook" }),
				isVisible: fbLink,
				increaseImpressionsOrder: 2,
			},
		}),
		[
			contactEmail,
			fbLink,
			instaLink,
			shouldIncreaseImpressions,
			trackingLink,
			twitterLink,
		]
	);

	if (!hasActions) return null;

	if (shouldIncreaseImpressions) {
		return (
			<section className="order-confirmation-action">
				{trackingLink && (
					<PaddedContainer marginBottom={SPACING.REGULAR}>
						<ThemeButton
							{...actions.track_order}
							style={{ marginLeft: 0 }}
						/>
					</PaddedContainer>
				)}
				<PaddedContainer
					flexContent
					className="order-confirmation-action-inline"
				>
					<ThemeButton onClick={handleMinimize}>
						Show Order Details
					</ThemeButton>
					<PaddedContainer marginLeft="auto" flexContent>
						{Object.entries(actions)
							.sort(
								([, a], [, b]) =>
									a.increaseImpressionsOrder -
									b.increaseImpressionsOrder
							)
							.map(
								([idx, { isVisible, ...rest }]) =>
									isVisible && (
										<ThemeButton key={idx} {...rest} />
									)
							)}
					</PaddedContainer>
				</PaddedContainer>
			</section>
		);
	}

	return (
		<section className="order-confirmation-action">
			{Object.entries(actions).map(
				([idx, { isVisible, ...rest }]) =>
					isVisible && <ThemeButton key={idx} {...rest} />
			)}
		</section>
	);
});

const OrderConfirmationDetails = memo(() => {
	const { scrapedInformation } = useData();

	const { shouldIncreaseImpressions } = useExperience();

	const { name, step, trackingInfo, identifier, cost } = useMemo(
		() => scrapedInformation || {},
		[scrapedInformation]
	);
	const orderConfirmationText = useMemo(
		() => (
			<>
				Your order {step === "delivered" ? "has been" : "is"}{" "}
				{step ? step : "confirmed"}.
			</>
		),
		[step]
	);

	if (shouldIncreaseImpressions) {
		return (
			<>
				<div className="order-confirmation-text">
					{!step || step === "confirmed" ? "Thank you! " : ""}
					{orderConfirmationText}
				</div>
				{identifier && (
					<div className="order-confirmation-number">
						Order #{identifier}{" "}
						{cost && (
							<>
								| <span>{cost}</span>
							</>
						)}
					</div>
				)}
			</>
		);
	}

	return (
		<>
			{identifier && (
				<div className="order-confirmation-number">
					Order #{identifier}
				</div>
			)}
			<div className="order-confirmation-text">
				Thank you
				<span>{name ? `, ${name}` : ""}!</span>
				<br />
				{orderConfirmationText}
			</div>

			{trackingInfo && (
				<div className="order-confirmation-tracking-info">
					{trackingInfo}
				</div>
			)}
		</>
	);
});
const OrderConfirmation = memo(() => {
	const { shouldIncreaseImpressions } = useExperience();
	return (
		<section
			className={genClassName({
				base: "order-confirmation-container",
				conditionals: {
					"order-confirmation-container-compact":
						shouldIncreaseImpressions,
				},
			})}
		>
			<OrderConfirmationDetails />
			<OrderConfirmationAction />
		</section>
	);
});

export default OrderConfirmation;
